import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Select, MenuItem, FormControl, InputLabel, CircularProgress,
  Snackbar, Alert, InputAdornment, Divider, Avatar, Button,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TablePagination, CardContent, Tooltip,
} from '@mui/material';
import { Visibility, Search, Refresh } from '@mui/icons-material';
import { InvokeApi, apiList } from '../services/ApiService';
import { Config } from '../config/Config';
import { formatDateTime } from '../utils/dateUtils';

const statusColor = { open: 'error', in_progress: 'warning', resolved: 'success' };
const priorityColor = { high: 'error', medium: 'warning', low: 'default' };

export default function Complaints() {
  const [rows, setRows]         = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [page, setPage]         = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch]     = useState('');
  const [loading, setLoading]   = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [resolution, setResolution] = useState('');
  const [saving, setSaving]     = useState(false);
  const [snack, setSnack]       = useState({ open: false, msg: '', sev: 'success' });

  // ── Fetch all complaints once ─────────────────────────────────────────────
  const fetchComplaints = useCallback(async () => {
    setLoading(true);
    const res = await InvokeApi(Config.base_url + apiList.getComplaints, {
      page: 1, per_page: 500, status: '', search: '',
    });
    if (res?.code === '200') {
      setRows((res.complaints ?? []).map((r, i) => ({ ...r, id: r.id ?? i + 1 })));
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchComplaints(); }, [fetchComplaints]);

  // ── Apply local filters (search + status) ────────────────────────────────
  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(
      rows.filter((r) => {
        const matchSearch =
          !q ||
          r.user_name?.toLowerCase().includes(q) ||
          r.category?.toLowerCase().includes(q) ||
          r.phone?.includes(q);
        const matchStatus = !statusFilter || r.status === statusFilter;
        return matchSearch && matchStatus;
      })
    );
    setPage(0);
  }, [search, statusFilter, rows]);

  const paginated = filtered.slice(page * pageSize, page * pageSize + pageSize);

  const handleView = (row) => {
    setSelected(row);
    setNewStatus(row.status || 'open');
    setResolution(row.resolution_note || '');
    setDialogOpen(true);
  };

  const handleUpdate = async () => {
    setSaving(true);
    const res = await InvokeApi(Config.base_url + apiList.updateComplaint, {
      id: selected.id, status: newStatus, resolution_note: resolution,
    });
    setSaving(false);
    if (res?.code === '200' || res?.status === 'success') {
      setSnack({ open: true, msg: 'Complaint updated.', sev: 'success' });
      setDialogOpen(false); fetchComplaints();
    } else { setSnack({ open: true, msg: res?.message || 'Update failed.', sev: 'error' }); }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Complaints</Typography>
          <Typography variant="body2" color="text.secondary">
            {filtered.length} complaint{filtered.length !== 1 ? 's' : ''} found
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={() => fetchComplaints()} sx={{ color: '#1a237e', border: '1px solid #e8eaf6', borderRadius: 2 }}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Filters — same layout as Users page */}
      <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', mb: 3 }}>
        <CardContent sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search user, phone or category..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Refresh">
            <IconButton onClick={() => fetchComplaints()} sx={{ color: '#1a237e', border: '1px solid #e8eaf6', borderRadius: 2 }}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </CardContent>
      </Card>

      {/* Table */}
      <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress sx={{ color: '#1a237e' }} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: '#f5f6fa' }}>
                    {['#ID', 'User', 'Phone', 'Category', 'Priority', 'Status', 'Description', 'Created', 'Actions'].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13, whiteSpace: 'nowrap' }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                        No complaints found
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginated.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>
                          <Chip
                            label={`#${row.id}`}
                            size="small"
                            sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 700, fontSize: 12 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar
                              sx={{
                                width: 34, height: 34,
                                background: 'linear-gradient(135deg,#1a237e,#1565c0)',
                                color: '#fff', fontWeight: 700, fontSize: 13,
                              }}
                            >
                              {(row.user_name || 'U')[0].toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography fontWeight={600} fontSize={13}>{row.user_name || 'Unknown'}</Typography>
                              <Typography fontSize={11} color="text.secondary">ID: {row.id}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{row.phone || '—'}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{row.category || '—'}</TableCell>
                        <TableCell>
                          <Chip label={row.priority || 'N/A'} color={priorityColor[row.priority] || 'default'} size="small" />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={(row.status || 'open').replace('_', ' ')}
                            color={statusColor[row.status] || 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 180, fontSize: 12 }}>
                            {row.description || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>
                          {row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => handleView(row)} sx={{ color: '#1a237e' }}>
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{ borderTop: '1px solid #f0f0f0', color: '#1a237e' }}
            />
          </>
        )}
      </Card>

      {/* Detail / Update Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e' }}>
          Complaint #{selected?.id}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
              <Chip label={selected?.priority || 'N/A'} color={priorityColor[selected?.priority] || 'default'} size="small" />
              <Chip label={(selected?.status || 'open').replace('_', ' ')} color={statusColor[selected?.status] || 'default'} size="small" />
              <Chip label={selected?.category || 'N/A'} variant="outlined" size="small" />
            </Box>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>User:</strong> {selected?.user_name} — {selected?.phone}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Submitted:</strong> {selected?.created_at ? formatDateTime(selected.created_at) : '—'}
            </Typography>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Description</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
              {selected?.description || '—'}
            </Typography>
            {selected?.photo_url && (
              <Box
                component="img"
                src={selected.photo_url.startsWith('http') ? selected.photo_url : `${Config.base_url}/${selected.photo_url}`}
                alt="complaint photo"
                sx={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 1, mt: 1.5 }}
              />
            )}
          </Box>
          <Divider sx={{ mb: 2 }} />
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Update Status</InputLabel>
            <Select value={newStatus} label="Update Status" onChange={(e) => setNewStatus(e.target.value)}>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth multiline rows={3} size="small" label="Resolution Note"
            value={resolution} onChange={(e) => setResolution(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained" onClick={handleUpdate} disabled={saving}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', fontWeight: 700 }}
          >
            {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack({ ...snack, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.sev} onClose={() => setSnack({ ...snack, open: false })}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
